<script setup>
import { watch, ref, reactive, defineProps, defineEmits, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";
import router from "@/router";
import DevicesList from "./DevicesList.vue";
import DeviceView from "./DeviceView.vue";
import BreadcrumbSeperator from "@/components/BreadcrumbSeperator.vue";

const props = defineProps({
  license: Object,
});
const emit = defineEmits(["Back"]);

const updates = reactive([]);
const loading = ref(false);
const selectedView = ref("Updates");
const selectedDevice = ref({});

const selectedUpdate = ref(null);
const searchDevices = ref("");

onMounted(() => {
  getData();
});

function processUpdates(data) {
  // Process the updates data here
  let result = [];
  data.forEach((device) => {
    device.updatesAvailable.forEach((update) => {
      let idVer = update.appID + update.version;
      if (!result.find((item) => item.idVer === idVer)) {
        result.push({
          idVer: idVer,
          appID: update.appID,
          name: update.definition.name,
          version: update.version,
          devicesCount: 1,
          devices: [device],
        });
      } else {
        let existing = result.find((item) => item.idVer === idVer);
        existing.devicesCount++;
        existing.devices.push(device);
      }
    });
    // if (!result.find((item) => item.idVer === idVer)) {
    //   result.push({

    //   });
    // } else {
    //   let existing = result.find((item) => item.idVer === idVer);
    //   existing.devices.push(device);
    // }
  });
  return result;
}

function selectDevice(_, row) {
  selectedDevice.value = row.item;
  selectedView.value = "Device";
}

function selectUpdate(_, row) {
  console.log("Selecting update", _, row);
  selectedUpdate.value = row.item;
  selectedView.value = "Computers";
}

async function getData() {
  loading.value = true;
  try {
    updates.length = 0; // Clear previous updates
    let result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getPatchingDashboard",
      id: props.license._id,
      dataType: "updatesPending",
    });
    if (result.result.length) {
      updates.push(...processUpdates(result.result));
    }
  } catch (error) {
    console.error("Error fetching pending updates:", error);
  } finally {
    loading.value = false;
  }
}

const headers = [
  { title: "App Name", value: "name", sortable: true },
  { title: "Version", value: "version", sortable: true },
  {
    title: "Devices Count",
    value: "devicesCount",
    sortable: true,
    default: true,
  },
];

// Convert date time to relative time either seconds, minutes, hours, or days if more than 3 days return date
const formatDate = (date) => {
  const now = new Date();
  const diff = Math.abs(now - new Date(date));
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return new Date(date).toLocaleString();
  } else if (days > 0) {
    return `${days} days ago`;
  } else if (hours > 0) {
    if (hours === 1) {
      return "1 hour ago";
    }
    return `${hours} hours ago`;
  } else if (minutes > 0) {
    if (minutes === 1) {
      return "1 minute ago";
    }
    return `${minutes} minutes ago`;
  } else {
    return `${seconds} seconds ago`;
  }
};
</script>

<template>
  <v-window v-model="selectedView">
    <v-window-item value="Device">
      <div style="display: flex; align-items: center" class="mb-2">
        <BreadcrumbEmit label="Dashboard" @click="emit('Back')" />
        <BreadcrumbSeperator />
        <BreadcrumbEmit
          label="Updates Pending"
          @click="selectedView = 'Updates'"
        />
        <BreadcrumbSeperator />
        <BreadcrumbEmit
          :label="selectedUpdate.name + ' ' + selectedUpdate.version"
          @click="selectedView = 'Computers'"
        />
        <BreadcrumbSeperator />
        <BreadcrumbEmit :label="selectedDevice.computerName" notLink />
      </div>

      <DeviceView
        :device="selectedDevice"
        :licenseId="props.license._id"
        @back="selectedView = 'Updates'"
      />
    </v-window-item>
    <v-window-item value="Updates">
      <div style="display: flex; align-items: center" class="mb-2">
        <BreadcrumbEmit label="Dashboard" @click="emit('Back')" />
        <BreadcrumbSeperator />
        <BreadcrumbEmit label="Updates Pending" notLink />
      </div>
      <h1 class="ma-1">Updates Pending</h1>
      <v-row>
        <v-col cols="3">
          <v-sheet rounded class="ma-1" elevation="1">
            <v-text-field
              v-model="searchDevices"
              hide-details
              prepend-inner-icon="mdi-magnify"
              density="compact"
              variant="solo"
              placeholder="Search anything..."
              clearable
            ></v-text-field>
          </v-sheet>
        </v-col>
      </v-row>
      <v-sheet rounded="lg" elevation="1" class="ma-1">
        <v-data-table
          :headers="headers"
          :items="updates"
          :loading="loading"
          item-value="idVer"
          @click:row="selectUpdate"
        />
      </v-sheet>
    </v-window-item>
    <v-window-item value="Computers">
      <div style="display: flex; align-items: center" class="mb-2">
        <BreadcrumbEmit label="Dashboard" @click="emit('Back')" />
        <BreadcrumbSeperator />
        <BreadcrumbEmit
          label="Updates Pending"
          @click="selectedView = 'Updates'"
        />
        <BreadcrumbSeperator />
        <BreadcrumbEmit
          :label="selectedUpdate.name + ' ' + selectedUpdate.version"
          notLink
        />
      </div>
      <h1>Updates Pending</h1>
      <h3>{{ selectedUpdate.name }}</h3>
      <v-data-table
        :headers="[
          { title: 'Device Name', value: 'computerName' },
          { title: 'Username', value: 'username' },
          { title: 'Last Contact', value: 'lastContact', sortable: true },
          { title: 'Configuration Group', value: 'configurationGroup' },
        ]"
        :items="selectedUpdate.devices || []"
        :loading="loading"
        @click:row="selectDevice"
      >
        <template v-slot:[`item.lastContact`]="{ item }">
          {{ formatDate(item.lastContact) }}
        </template>
      </v-data-table>
    </v-window-item>
  </v-window>
</template>
1
